import { REGIONS_BY_ID, type Region } from '@snootsvet/shared/portal'

import { useGetMe } from '@/customer/hooks/use-get-me'

/**
 * Returns the current member's Region config, driving locale, currency,
 * timezone, and unit conventions across the portal. An override is returned
 * directly and disables internal fetches.
 *
 * Falls back to the UK region when the `me` snapshot hasn't loaded (or
 * the caller sits outside the authenticated portal, e.g. the public
 * booking flow, which should source its region from the clinic
 * instead). Non-nullable so consumers can call region-aware formatters
 * without null-guarding on every render.
 *
 * Future: swap the fallback for dynamic region detection - URL params
 * from marketing landing pages, browser locale, geo-IP hints - so
 * non-authed surfaces pick the right region before a member exists.
 */
type UseRegionOptions = {
  /**
   * Supplies a region already resolved by the current context, such as a public booking clinic.
   * Avoids requesting the member profile when no signed-in member is available.
   */
  override?: Region
}

export function useRegion({ override }: UseRegionOptions = {}): Region {
  const me = useGetMe({ enabled: !override })
  if (override) return override

  const id = me.data?.member.region
  return id ? REGIONS_BY_ID[id] : REGIONS_BY_ID.uk
}
